/**
 * Game catalog and game-session state via TanStack Query (docs/APP_PLAN.md
 * §5.2, §12 M3), following the same pattern as `lib/auth.ts`. This is the
 * *only* place `apps/web` talks to `/api/games/*` and
 * `/api/game-sessions/*` — feature components call these hooks rather than
 * `apiClient` directly, so query keys and cache-update behavior live in
 * exactly one place.
 *
 * Nothing here is game-specific: `submitAction`'s `action` parameter and a
 * session's `view` are both `unknown` as far as this module is concerned
 * (TECH_STACK §10) — only the resolved game component
 * (`apps/web/src/games/<game-id>`) interprets them.
 */
import { useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { GAME_HISTORY_LIMIT } from '@perplexicon/config';
import { ApiError } from '@perplexicon/api-client';
import type {
  CreateLobbySessionInput,
  CreateMultiplayerSessionInput,
  GameCatalogEntry,
  GameSession,
  JoinSessionByCodeInput,
  SessionPresenceFrame,
  SessionSummary,
} from '@perplexicon/api-client';

import { apiClient } from './api-client.js';
import { INVITATIONS_INCOMING_QUERY_KEY, INVITATIONS_OUTGOING_QUERY_KEY } from './invitations.js';
import {
  isSessionStreamDegraded,
  nextConsecutiveErrorCount,
  shouldInvalidateOnRevision,
} from './session-stream-state.js';
import {
  ACTIVE_SESSIONS_QUERY_KEY,
  COMPLETED_SESSIONS_QUERY_KEY,
  GAME_CATALOG_QUERY_KEY,
  GAME_SESSIONS_LIST_QUERY_KEY,
  SESSION_HISTORY_QUERY_KEY,
  gameSessionQueryKey,
} from '../features/games/query-keys.js';

/** Polling interval for the dashboard's active-games list, so whose-turn/last-activity updates without a manual refresh (TECH_STACK §49: refetch, not WebSockets). */
const ACTIVE_SESSIONS_POLL_MS = 10_000;

/**
 * Session-status-aware polling interval for a single open game session
 * (docs/games/word-rush/GAME_PLAN.md §13.7, user decision 5): 2s while the
 * session is genuinely `active` (a lobby roster/ready toggle, or an
 * opponent's move/turn, should show up quickly without a manual refresh),
 * 3s otherwise (`pending` — waiting on an invitation), stopped entirely
 * once `completed` — a finished session has nothing left to wait for.
 * TanStack Query's default `refetchIntervalInBackground: false` means
 * hidden tabs do not poll either.
 */
const GAME_SESSION_ACTIVE_POLL_MS = 2_000;
const GAME_SESSION_INACTIVE_POLL_MS = 3_000;

export function useGameCatalog(): UseQueryResult<GameCatalogEntry[]> {
  return useQuery({
    queryKey: GAME_CATALOG_QUERY_KEY,
    queryFn: () => apiClient.games.list(),
  });
}

/** `GET /api/game-sessions?status=active` — the dashboard's "active games" section (APP_SPEC §18). */
export function useActiveSessions(): UseQueryResult<SessionSummary[]> {
  return useQuery({
    queryKey: ACTIVE_SESSIONS_QUERY_KEY,
    queryFn: () => apiClient.gameSessions.list({ status: 'active' }),
    refetchInterval: ACTIVE_SESSIONS_POLL_MS,
  });
}

/** `GET /api/game-sessions?status=completed` — the dashboard's "recent completed games" section (APP_SPEC §18). */
export function useCompletedSessions(): UseQueryResult<SessionSummary[]> {
  return useQuery({
    queryKey: COMPLETED_SESSIONS_QUERY_KEY,
    queryFn: () => apiClient.gameSessions.list({ status: 'completed', limit: 10 }),
  });
}

/**
 * `GET /api/game-sessions?status=completed` at the full Game History window
 * — the History page (APP_SPEC §44, docs/APP_PLAN.md §12 M6). A distinct
 * cache entry from the dashboard's shorter list above, since the two carry
 * different result sizes for the same status.
 *
 * `GAME_HISTORY_LIMIT` is the same constant the API enforces
 * (`@perplexicon/config`), so this asks for exactly the rolling window the
 * server is willing to return — the limiting happens in the database
 * query, never by trimming a longer list here.
 */
export function useSessionHistory(): UseQueryResult<SessionSummary[]> {
  return useQuery({
    queryKey: SESSION_HISTORY_QUERY_KEY,
    queryFn: () => apiClient.gameSessions.list({ status: 'completed', limit: GAME_HISTORY_LIMIT }),
  });
}

export function useGameSession(sessionId: string | undefined): UseQueryResult<GameSession> {
  return useQuery({
    queryKey: gameSessionQueryKey(sessionId ?? ''),
    queryFn: () => apiClient.gameSessions.get(sessionId as string),
    enabled: sessionId !== undefined,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed') return false;
      return status === 'active' ? GAME_SESSION_ACTIVE_POLL_MS : GAME_SESSION_INACTIVE_POLL_MS;
    },
  });
}

/**
 * Creates a solo session and seeds its query cache so `/play/:sessionId`
 * never refetches on first paint.
 *
 * Also invalidates every session list: the game the player just started is
 * now one of their active games, and the catalog's "Active" chip, the
 * dashboard's "Continue playing" section and the Active Games page all read
 * those lists. Without this they would keep serving the pre-creation answer
 * for up to the client's 30s `staleTime` — showing "nothing in progress"
 * moments after starting a game.
 */
export function useCreateSoloSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (gameId: string) => apiClient.gameSessions.createSolo({ gameId }),
    onSuccess: (session) => {
      queryClient.setQueryData(gameSessionQueryKey(session.id), session);
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_LIST_QUERY_KEY });
    },
  });
}

/**
 * Creates a `pending` multiplayer session plus its invitation (the
 * create-multiplayer-game flow, APP_SPEC §19-§20). Invalidates the
 * outgoing-invitations list so it appears there immediately rather than
 * waiting for the next poll.
 */
export function useCreateMultiplayerSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMultiplayerSessionInput) =>
      apiClient.gameSessions.createMultiplayer(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_OUTGOING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_LIST_QUERY_KEY });
    },
  });
}

/**
 * Creates an `active` `joinModel: 'open_lobby'` session (docs/games/
 * word-rush/GAME_PLAN.md §13.2, W1) — host seated immediately, a join code
 * minted, and an invitation + notification sent to each named invitee.
 * Seeds the new session's own query cache and invalidates the outgoing-
 * invitations list, exactly like `useCreateMultiplayerSession` above.
 */
export function useCreateLobbySession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLobbySessionInput) => apiClient.gameSessions.createLobby(input),
    onSuccess: (session) => {
      queryClient.setQueryData(gameSessionQueryKey(session.id), session);
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_OUTGOING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_LIST_QUERY_KEY });
    },
  });
}

/**
 * Joins an open lobby by its 6-character code (docs/games/word-rush/
 * GAME_PLAN.md §13.2, W3). Seeds the joined session's own query cache so
 * `/play/:sessionId` never refetches on first paint.
 */
export function useJoinSessionByCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: JoinSessionByCodeInput) => apiClient.gameSessions.joinByCode(input),
    onSuccess: (session) => {
      queryClient.setQueryData(gameSessionQueryKey(session.id), session);
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_LIST_QUERY_KEY });
    },
  });
}

/**
 * Submits an action for `session`, attaching its current `expectedVersion`
 * (docs/APP_PLAN.md §8.3). On success, the cache is updated with the new
 * authoritative view. On `STALE_STATE`, the server's attached fresh view
 * (`ApiError.details`) is written to the cache instead of being discarded,
 * so the UI reconciles to the true state without an extra round trip.
 */
export function useSubmitGameAction(session: GameSession | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: unknown) => {
      if (!session) throw new Error('No active session to submit an action for.');
      return apiClient.gameSessions.submitAction(session.id, {
        expectedVersion: session.version,
        action,
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(gameSessionQueryKey(updated.id), updated);
    },
    onError: (error) => {
      if (!session) return;
      if (error instanceof ApiError && error.code === 'STALE_STATE' && error.details) {
        queryClient.setQueryData(gameSessionQueryKey(session.id), error.details);
      }
    },
  });
}

/**
 * Resigns/forfeits `session` (docs/APP_PLAN.md §8.6, §12 M6): in
 * multiplayer the caller loses and the opponent wins; in solo the session
 * is recorded as abandoned. Updates the session's own cache entry with the
 * now-completed result and invalidates every session-list query (active,
 * dashboard's completed, and the History page's) so they stop showing the
 * game as in progress without waiting for their next poll.
 */
export function useForfeitSession(session: GameSession | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!session) throw new Error('No active session to forfeit.');
      return apiClient.gameSessions.forfeit(session.id);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(gameSessionQueryKey(updated.id), updated);
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_LIST_QUERY_KEY });
    },
  });
}

/**
 * Abandons the session currently occupying a concurrency slot (APP_SPEC
 * §21.1), so the caller can start or join another game of the same kind.
 * The server decides whether that means forfeiting an active session or
 * cancelling a pending one — the client never branches on session status.
 *
 * Invalidates every session-list query plus both invitation lists: a
 * cancelled pending session resolves its outstanding invitations too, so
 * an unrefreshed "sent invitations" list would keep offering a game that
 * no longer exists.
 */
export interface UseSessionStreamResult {
  /**
   * `true` once two consecutive `EventSource` failures have occurred for
   * this session (docs/games/word-delve/GAME_PLAN.md §15.1: "two-strike
   * degradation") — the hook has stopped subscribing entirely, and the
   * game keeps working exactly as it did before this milestone via the
   * existing 2s poll (`useGameSession` above). This milestone only surfaces
   * the state; a later one (M6) renders a non-blocking notice from it.
   */
  degraded: boolean;
  /**
   * The latest `presence` frame received per seat (docs/games/word-delve/
   * GAME_PLAN.md §17.2, D12) — a seat's entry is simply replaced by its own
   * next frame, "latest wins," and a seat with no frame yet (or whose
   * connection is degraded) is absent entirely. This is generic, game-
   * agnostic wire data (a seat number, cell indices, a timestamp) — this
   * hook never interprets what the cells mean; only a game's own UI
   * (`PeerPathLayer.tsx`) does that. Empty for any game that never posts
   * presence, or while `enabled` is `false`.
   */
  presenceBySeat: Readonly<Record<number, SessionPresenceFrame>>;
}

/**
 * Opt-in real-time supplement to `useGameSession`'s poll (docs/games/
 * word-delve/GAME_PLAN.md §15.1) — never a replacement for it. Subscribes
 * to `GET /api/game-sessions/:id/stream` only while `enabled` (a game
 * declaring `metadata.realtime`, on its play screen); every other game's
 * screen never calls this, and the server 404s the route anyway for a
 * session whose game omits `metadata.realtime`, which this hook treats
 * exactly like any other connection failure (a strike toward degradation).
 *
 * On a `revision` frame whose version exceeds the session's currently
 * cached version, invalidates the session's query key so the *next*
 * authoritative fetch still goes through the normal `GET` ->
 * `toSessionResponse` -> `getPlayerView` path — this hook never itself
 * carries or exposes authoritative payload data, only an integer version
 * and a timestamp.
 */
export function useSessionStream(
  sessionId: string | undefined,
  enabled: boolean,
): UseSessionStreamResult {
  const queryClient = useQueryClient();
  // Tracks WHICH session most recently degraded, rather than a plain
  // boolean that would need an explicit reset when `sessionId` changes: a
  // fresh session is simply never equal to `degradedSessionId`, so
  // navigating from a degraded session to a new one is automatically
  // "not degraded" with no separate reset call (avoiding a synchronous
  // `setState` at the top of the effect body).
  const [degradedSessionId, setDegradedSessionId] = useState<string | undefined>(undefined);
  const [presenceBySeat, setPresenceBySeat] = useState<Record<number, SessionPresenceFrame>>({});

  useEffect(() => {
    if (!sessionId || !enabled) return undefined;

    let consecutiveErrors = 0;
    let stopped = false;

    const unsubscribe = apiClient.gameSessions.subscribeToSession(sessionId, {
      onOpen: () => {
        consecutiveErrors = 0;
        // Cleared here (the moment a connection for THIS session is
        // actually confirmed open, including after a reconnect) rather
        // than synchronously at the top of the effect body -- avoids the
        // cascading-render pattern of an unconditional `setState` call in
        // an effect (react-hooks/set-state-in-effect), and means a brand
        // new session never starts out carrying a stale seat's presence
        // frame from whatever session this hook was previously subscribed
        // to.
        setPresenceBySeat({});
      },
      onRevision: (frame) => {
        const cached = queryClient.getQueryData<GameSession>(gameSessionQueryKey(sessionId));
        if (shouldInvalidateOnRevision(cached?.version, frame.version)) {
          void queryClient.invalidateQueries({ queryKey: gameSessionQueryKey(sessionId) });
        }
      },
      onPresence: (frame) => {
        setPresenceBySeat((current) => ({ ...current, [frame.seat]: frame }));
      },
      onError: () => {
        if (stopped) return;
        consecutiveErrors = nextConsecutiveErrorCount(consecutiveErrors);
        if (isSessionStreamDegraded(consecutiveErrors)) {
          stopped = true;
          setDegradedSessionId(sessionId);
          unsubscribe();
        }
      },
    });

    return () => {
      stopped = true;
      unsubscribe();
    };
  }, [sessionId, enabled, queryClient]);

  return {
    degraded: sessionId !== undefined && degradedSessionId === sessionId,
    presenceBySeat,
  };
}

/**
 * Posts this client's own cosmetic in-progress path (docs/games/word-delve/
 * GAME_PLAN.md §15.1, D12) — `POST /api/game-sessions/:id/presence`. Never
 * persisted, never authoritative (CLAUDE.md §3): a game's own UI is
 * responsible for throttling how often it calls the returned function
 * (well under `REALTIME_RATE_LIMITS.presence`, `@perplexicon/config`) —
 * this hook only wraps the network call, exactly like every other mutation
 * in this file. Errors are swallowed rather than surfaced as `actionError`:
 * a failed presence post never blocks or degrades gameplay (it is not the
 * `submitAction` path), it simply means a teammate's peer-path view is
 * momentarily stale, which `PeerPathLayer`'s own fade-out already handles.
 */
export function usePostPresence(sessionId: string | undefined) {
  return useMutation({
    mutationFn: (cells: number[]) => {
      if (!sessionId) throw new Error('No active session to post presence for.');
      return apiClient.gameSessions.postPresence(sessionId, cells);
    },
  });
}

export function useAbandonSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => apiClient.gameSessions.abandon(sessionId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_LIST_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_INCOMING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: INVITATIONS_OUTGOING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: gameSessionQueryKey(result.sessionId) });
    },
  });
}
